<template>
    <div class="notifications-panel">
        <div class="page-notifications">
            <div v-for="(notification, index) in displayNotifications" :key="notification.eventId || index"
                class="notification-item" :class="getNotificationClass(notification)"
                @click="handleNotificationClick(notification)">
                <!-- 溫度顯示（僅溫度通知） -->
                <div class="notification-extra" v-if="notification.temperature">
                    <div class="temperature">
                        <Icon type="ios-thermometer-outline" />
                        {{ notification.temperature }}
                        <span>℃</span>
                    </div>
                </div>

                <div class="notification-content">
                    <div class="notification-badges" v-if="!notification.temperature">
                        <span class="badge badge-priority" :class="notification.priority">
                            {{ getPriorityText(notification.priority) }}
                        </span>
                    </div>

                    <div class="notification-main">
                        <span class="field-name">{{ notification.fieldName }}</span>
                        <span class="message">{{ notification.message }}</span>
                    </div>
                </div>

                <div class="notification-action" @click="NotificationCloseClick(notification, $event)">
                    <Icon type="md-close" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { eventBus } from '@shared/stores/globalState';
import eventService from '@shared/services/event';

export default {
    name: 'PageNotifications',
    data() {
        return {
            eventServiceListener: null,
            cachedEvents: [],
            updateTrigger: 0
        };
    },
    computed: {
        displayNotifications() {
            // 依賴 updateTrigger 來強制重新計算
            this.updateTrigger;

            // 轉換為顯示格式
            return eventService?.convertEventsToFormat(this.cachedEvents || [], 'display') || [];
        }
    },
    methods: {
        // 載入通知事件
        async loadNotifications() {
            if (!eventService?.isInitialized) {
                return;
            }

            try {
                // 使用 query API 查詢高優先級和中優先級的未讀、未解決事件
                const events = await eventService.query()
                    .lastDays(7) // 只顯示最近 7 天的
                    .unread() // 未讀
                    .unresolved() // 未解決
                    .execute();

                // 篩選高優先級和中優先級，排除風險類型
                const filteredEvents = events.filter(e =>
                    (e.priority === 'high' || e.priority === 'medium') &&
                    e.type !== 'risk'
                );

                // 排序並取前 10 個
                this.cachedEvents = filteredEvents
                    .sort((a, b) => {
                        // 先按優先級排序（high > medium）
                        if (a.priority !== b.priority) {
                            return a.priority === 'high' ? -1 : 1;
                        }
                        // 再按時間排序（新的在前）
                        return new Date(b.created_at) - new Date(a.created_at);
                    })
                    .slice(0, 10);

                this.updateTrigger++;

            } catch (error) {
                console.error('[PageNotifications] 載入通知失敗:', error);
                this.cachedEvents = [];
            }
        },

        // 設置 EventService 監聽器
        setupEventServiceListener() {
            if (!eventService) {
                return;
            }

            this.eventServiceListener = async (eventType) => {
                if (eventType === 'event:added' ||
                    eventType === 'event:mqtt' ||
                    eventType === 'event:read' ||
                    eventType === 'event:deleted') {
                    // 重新載入通知
                    await this.loadNotifications();
                }
            };

            eventService.addEventListener(this.eventServiceListener);
        },

        // 移除 EventService 監聽器
        removeEventServiceListener() {
            if (this.eventServiceListener && eventService) {
                eventService.removeEventListener(this.eventServiceListener);
                this.eventServiceListener = null;
            }
        },

        getNotificationClass(notification) {
            return {
                [`type-${notification.type}`]: true,
                [`priority-${notification.priority}`]: true,
                'has-temperature': !!notification.temperature
            };
        },
        getTypeText(type) {
            return eventService?.getEventTypeLabel(type) || '通知';
        },
        getPriorityText(priority) {
            return eventService?.getPriorityLabel(priority) || '一般';
        },
        async handleNotificationClick(notification) {
            // 標記事件為已讀 (如果有 eventId)
            if (notification.eventId && eventService) {
                try {
                    await eventService.markAsRead(notification.eventId);
                } catch (error) {
                    console.error('標記通知已讀失敗:', error);
                }
            }

            // 開啟事件詳情
            this.openEventDetail(notification);
        },

        // 開啟事件詳情
        openEventDetail(notification) {
            if (notification.eventId) {
                eventBus.emit('ui:open-event-detail', {
                    eventId: notification.eventId,
                    title: notification.title || '事件詳情'
                });
            }
        },

        // 處理通知關閉點擊
        async NotificationCloseClick(notification, event) {
            try {
                // 阻止事件冒泡，避免觸發 handleNotificationClick
                if (event) {
                    event.stopPropagation();
                }

                // 如果通知有對應的事件ID，標記為已讀
                if (notification.eventId && eventService) {
                    await eventService.markAsRead(notification.eventId);
                    // 標記為已讀後，會觸發 event:read 事件，自動更新列表
                }

                // 發送關閉事件給父組件
                this.$emit('notification-close', notification);

            } catch (error) {
                console.error('[PageNotifications] 關閉通知失敗:', error);
            }
        },

        // 處理 EventService 狀態變化
        handleEventServiceStatusChange(status) {
            if (status === 'connected' && eventService?.isInitialized) {
                // 服務已連接，設置監聽器（只設置一次）
                if (!this.eventServiceListener) {
                    this.setupEventServiceListener();
                    this.loadNotifications();
                }
            }
        }
    },

    async mounted() {
        this.eventBus.on('eventService:statusChanged', this.handleEventServiceStatusChange);
        // 監聽 App.vue 發送的通知刷新事件（通知策略系統）
        this.eventBus.on('pageNotifications:refresh', this.loadNotifications);

        try {
            // 如果 EventService 已經可用且已初始化，直接設置監聽器並載入通知
            if (eventService?.isInitialized) {
                this.setupEventServiceListener();
                await this.loadNotifications();
            }
        } catch (error) {
            console.error('[PageNotifications] 初始化失敗:', error);
        }
    },

    beforeUnmount() {
        this.removeEventServiceListener();
        this.eventBus.off('eventService:statusChanged', this.handleEventServiceStatusChange);
        this.eventBus.off('pageNotifications:refresh', this.loadNotifications);
    }
};
</script>

<style scoped lang="stylus">

// 通知面板樣式
.notifications-panel
    // todo: 隱藏, 功能尚未完成
    // display: none
    position: fixed
    top: 80px
    right: 20px
    width: 400px
    max-height: calc(100vh - 120px)
    background: transparent
    z-index: 1000
    pointer-events: none
    
    // 讓內部的通知項目可以點擊
    ::v-deep(.page-notifications)
        pointer-events: auto
        
    ::v-deep(.notification-item)
        pointer-events: auto

// 響應式設計
@media (max-width: 768px)
    .notifications-panel
        top: 70px
        left: calc(50% - 200px)
        right: 15px
        width: auto
        max-height: calc(100vh - 80px)

.page-notifications
    display: flex
    flex-direction: column
    gap: 8px
    padding: 0
    max-width: 400px

.notification-item
    display: flex
    align-items: center
    padding: 12px 16px
    border-radius: 6px
    cursor: pointer
    transition: all 0.3s ease
    backdrop-filter: blur(10px)
    border: 1px solid rgba(255, 255, 255, 0.2)
    background: rgba(50, 50, 80, 0.9)
    color: white
    
    &:hover
        transform: translateY(-1px)
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3)
    
    // 一般警告類型背景色
    &.type-alarm,
    &.type-warning,
    &.type-info,
    &.type-condition,
    &.type-timeout
        // 樣式由優先級決定，此處留空或設置通用邊框
        border: 1px solid rgba(255, 255, 255, 0.1)

    // 根據優先級設定背景色
    &.priority-high
        background: linear-gradient(135deg, #d32f2f, #b71c1c)
        border-color: rgba(211, 47, 47, 0.4)

    &.priority-medium
        background: linear-gradient(135deg, #b48c34, #8b6b28)
        border-color: rgba(180, 140, 52, 0.4)
        
    // 溫度警告特殊樣式 (會覆蓋優先級顏色)
    &.has-temperature
        background: linear-gradient(135deg, #FF8C00, #FF6B35)
        border: 1px solid rgba(255, 140, 0, 0.4)
        position: relative
        
        .notification-extra
            display: flex
            position: static
            transform: none
            margin-right: 16px
            flex-shrink: 0

.notification-content
    flex: 1
    display: flex
    flex-direction: row
    gap: 8px
    position: relative
    height: 25px
    line-height: 25px
    overflow: hidden
    
    // 溫度通知的特殊佈局
    .has-temperature &
        margin-left: 0

.notification-badges
    display: flex
    gap: 6px

.badge
    padding: 3px 8px
    border-radius: 3px
    font-size: 12px
    font-weight: bold
    line-height: 20px
    
    &.badge-type
        background: #dc3545
        color: white
        
    &.badge-priority
        font-size: 10px
        border: 1px solid #f00
        background: #fff
        color: #333
        padding: 2px 5px
        font-size: 12px
        font-weight: bold
        line-height: 20px

    &.high
        border: 1px solid #f00
        background: #fff
        color: #f00
    &.medium
        border: 1px solid #9d4d6b
        background: #ffc107
        color: white
    &.low
        background: #28a745

.notification-main
    display: flex
    align-items: center
    gap: 8px
    font-size: 14px
    line-height: 20px
    span
        height: 20px
        overflow: hidden
    
    .field-name
        font-weight: bold
        color: white
        
    .message
        opacity: 0.95
        color: rgba(255, 255, 255, 0.9)

.extra-info
    height: 20px
    overflow: hidden
.notification-extra
    display: flex
    align-items: center
    gap: 8px
    font-size: 13px
    
    .temperature
        display: flex
        align-items: center
        gap: 6px
        font-size: 22px
        font-weight: 500
        color: white
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3)
        
        i
            font-size: 24px
            color: rgba(255, 255, 255, 0.95)
            text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3)
        span
            margin-top: 5px
            font-size: 16px
            font-weight: 900
            color: white
            text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3)

.notification-action
    display: flex
    align-items: center
    margin-left: auto
    
    .ivu-icon
        font-size: 24px
        color: #A0E600
        padding: 6px
        border-radius: 50%
        transition: all 0.3s ease
        
        &:hover
            background: #4CAF50
            color: white
            transform: scale(1.05)
            border-color: #4CAF50

// 響應式設計
@media (max-width: 768px)
    .page-notifications
        padding: 0
        gap: 6px
        
    .notification-item
        padding: 10px 12px
        
    .notification-main
        font-size: 13px
        gap: 6px
        
    .notification-badges
        gap: 4px
        
        .badge
            font-size: 10px
            padding: 2px 6px
        
    .notification-extra
        margin-right: 12px
        
        .temperature
            font-size: 26px
            gap: 4px
            
            i
                font-size: 22px
</style>